import { ReviewRoute, ReviewStop } from './confirm-review';
import { nearestStopKm } from './plan-partitioning';
import { DeliveryPoint } from './plan-result';

/**
 * Los pedidos que NO entraron al plan y aun así se pueden entregar, listos para
 * incorporarse a una ruta desde el gestor de confirmación.
 *
 * Existe porque un pedido sin flota no tenía ninguna salida en el gestor: la
 * pantalla nombraba el problema —*"asignarle un vehículo a la ruta en el
 * maestro"*— y ahí terminaba. El operador, mientras tanto, lo sube igual a un
 * camión que pasa cerca.
 *
 * **El territorio manda igual que en el movimiento entre rutas**: una ruta no
 * entrega donde no visita, y eso vale también para lo que se incorpora. Este
 * módulo NO decide compatibilidad —la decide `canRouteReceive`, la misma función
 * de siempre, que el llamador inyecta— sino que ORDENA las compatibles por cuánto
 * se desvía cada una.
 *
 * Por qué la regla sí puede aplicarse acá, cuando parecía que dejaría cero
 * destinos: **la partición y el gestor no leen los mismos territorios**. La
 * partición decide la brecha con `territoryIdsOf(order)`, la copia embebida en el
 * pedido; el gestor decide compatibilidad con los territorios del **maestro del
 * cliente**, que la copia puede traer recortada ("mi cliente tiene 5 rutas y solo
 * me deja la suya"). Un pedido queda sin flota porque su copia solo traía el
 * territorio sin camión, y su cliente igual puede pertenecer a uno que sí lo
 * tiene. Ahí es donde hay destino, y es el caso corriente.
 *
 * Cuando de verdad no hay ninguna ruta que visite al cliente, el pedido se sigue
 * mostrando **sin destinos y con el motivo**: eso no se arregla en el reparto sino
 * en el maestro de rutas.
 *
 * Módulo puro: entran los puntos del resultado y las rutas del gestor, salen
 * candidatos, el pool y un ranking. Sin HTTP, sin Angular.
 */

/** Un pedido incorporable, con el motivo por el que no entró. */
export interface PendingOrder {
  point: DeliveryPoint;
  /** Motivo, ya en el vocabulario de la pantalla. Decide si incorporarlo es razonable. */
  reason: string;
  /**
   * `true` si el pedido SÍ estaba en el plan y está acá porque alguien lo retiró.
   *
   * No es lo mismo que no haber entrado nunca: esos no tenían camión que los llevara,
   * y estos lo tenían y salieron del día por una decisión. La bandera existe porque el
   * aviso de "estos pedidos no se entregan" cuenta **solo** los retirados, y
   * distinguirlos comparando el texto del motivo sería frágil — cambiar una etiqueta
   * rompería un conteo que decide si se advierte que una entrega se cae.
   */
  fromPlan: boolean;
}

/** Una lista de pedidos que no entraron, bajo un mismo motivo. */
export interface PendingSource {
  reason: string;
  points: readonly DeliveryPoint[];
  /** `true` si estos puntos son paradas del PLAN (ver `PendingOrder.fromPlan`). */
  fromPlan?: boolean;
}

/** Los pendientes de un motivo, para el bloque colapsable del pool. */
export interface PendingGroup {
  reason: string;
  items: PendingOrder[];
}

/** Una ruta candidata a recibir un pedido, con lo que se desvía para recogerlo. */
export interface RouteDetour {
  route: ReviewRoute;
  /**
   * Kilómetros en línea recta a la parada más cercana de la ruta.
   *
   * `undefined` cuando no se puede medir —la ruta no tiene ninguna parada con
   * coordenada, o el pedido no la tiene—. **Nunca un cero de relleno**: un cero
   * la pondría primera en el ranking, que es exactamente al revés de lo que se
   * sabe.
   */
  detourKm?: number;
}

/** `[lat, lon]` de una parada del gestor, resuelto por el llamador. */
export type CoordOf = (orderId: string) => [number, number] | undefined;

/**
 * Los pedidos que se pueden incorporar, de todas las fuentes.
 *
 * Dos condiciones, y las dos por el mismo motivo —que incorporarlo sea la acción
 * correcta y no la que esconde un arreglo pendiente—:
 *
 * 1. **Coordenada válida**: sin ella no hay parada que agregar a un recorrido.
 * 2. **Al menos un territorio**: sin él el problema es la ficha del cliente, y
 *    meterlo en una ruta tapa una corrección del maestro.
 *
 * La segunda pregunta se DELEGA en vez de derivarse del motivo. La respuesta ya
 * la da `territoryRefsOf`, que es la que decide los destinos del arrastre y la que
 * restaura pertenencia: dos definiciones de "tiene territorio" divergirían, y el
 * síntoma sería que el pool ofrece lo que el rótulo de fuera-de-territorio no sabe
 * evaluar.
 *
 * Un pedido que aparece en dos fuentes sale UNA vez, con el motivo de la primera:
 * dos filas del mismo pedido dejarían incorporarlo dos veces y confirmar dos
 * visitas al mismo destino.
 */
export function pendingCandidates(
  sources: readonly PendingSource[] | undefined,
  hasTerritory: (orderId: string) => boolean,
): PendingOrder[] {
  const seen = new Set<string>();
  const out: PendingOrder[] = [];
  for (const source of sources ?? []) {
    for (const point of source.points ?? []) {
      const orderId = point?.orderId;
      if (!orderId || seen.has(orderId)) continue;
      if (point.lat === null || point.lon === null) continue;
      if (!hasTerritory(orderId)) continue;
      seen.add(orderId);
      out.push({ point, reason: source.reason, fromPlan: !!source.fromPlan });
    }
  }
  return out;
}

/**
 * Los candidatos que TODAVÍA no están en ninguna ruta del gestor.
 *
 * **Derivado y no mantenido.** La alternativa —un arreglo que se muta al
 * incorporar y al quitar— obliga a sincronizar dos representaciones del mismo
 * conjunto, y acá divergir significa incorporar dos veces el mismo pedido.
 * Derivándolo, "sale del pool", "vuelve al pool" y "restaurar la ruta lo
 * devuelve" caen de la definición y no necesitan código.
 */
export function pendingPool(
  candidates: readonly PendingOrder[],
  routes: readonly ReviewRoute[],
): PendingOrder[] {
  const placed = new Set<string>();
  for (const route of routes ?? []) {
    for (const stop of route.stops ?? []) placed.add(stop.orderId);
  }
  return (candidates ?? []).filter((c) => !placed.has(c.point.orderId));
}

/** Los pendientes agrupados por motivo, en el orden en que el motivo apareció. */
export function groupPendingByReason(pending: readonly PendingOrder[]): PendingGroup[] {
  const byReason = new Map<string, PendingGroup>();
  for (const item of pending ?? []) {
    const group = byReason.get(item.reason) ?? { reason: item.reason, items: [] };
    group.items.push(item);
    byReason.set(item.reason, group);
  }
  return [...byReason.values()];
}

/**
 * Las rutas que PUEDEN recibir el pedido, ordenadas por lo que se desvían para
 * recogerlo.
 *
 * `canReceive` es la puerta y la decide el llamador con `canRouteReceive` — la
 * MISMA función que gobierna el movimiento entre rutas. Dos definiciones de
 * "compatible" divergirían, y el síntoma sería que incorporar permite lo que
 * arrastrar prohíbe. Sin predicado no se filtra nada, que es lo que necesitan los
 * tests del ordenamiento puro.
 *
 * El desvío NO es la puerta: es cómo se elige entre las compatibles. Un pedido de
 * la brecha suele tener varias, porque su cliente pertenece a más territorios que
 * los que su copia embebida declara.
 *
 * Es distancia en LÍNEA RECTA, nunca minutos. El motor no evaluó esta secuencia, y
 * ponerle tiempo sería la misma mentira de precisión que el gestor ya rechaza para
 * las horas de llegada de un orden personalizado.
 *
 * Las que no se pueden medir van al final, en su orden de entrada.
 */
export function rankRoutesByDetour(
  routes: readonly ReviewRoute[],
  point: DeliveryPoint,
  coordOf: CoordOf,
  canReceive?: (route: ReviewRoute) => boolean,
): RouteDetour[] {
  const from: [number, number] | undefined =
    point?.lat === null || point?.lon === null || point?.lat === undefined || point?.lon === undefined
      ? undefined
      : [point.lat, point.lon];

  const eligible = canReceive ? (routes ?? []).filter((r) => canReceive(r)) : routes ?? [];

  const ranked = eligible.map((route, index) => {
    if (!from) return { route, index, detourKm: undefined as number | undefined };
    const stops = route.stops
      .map((s) => coordOf(s.orderId))
      .filter((c): c is [number, number] => !!c);
    return { route, index, detourKm: stops.length ? nearestStopKm(from, stops) : undefined };
  });

  ranked.sort((a, b) => {
    if (a.detourKm === undefined && b.detourKm === undefined) return a.index - b.index;
    if (a.detourKm === undefined) return 1;
    if (b.detourKm === undefined) return -1;
    return a.detourKm - b.detourKm || a.index - b.index;
  });

  return ranked.map(({ route, detourKm }) => ({ route, detourKm }));
}

/**
 * El punto del resultado como parada del gestor, ya marcada.
 *
 * Nace con `injected` puesto: la marca es un atributo DE LA PARADA, así que la
 * acompaña al reordenarla y desaparece con ella al restaurar. Un `Set` de ids en
 * el diálogo se desincronizaría con el primer movimiento.
 */
export function toReviewStop(point: DeliveryPoint): ReviewStop {
  return {
    orderId: point.orderId,
    name: point.name,
    address: point.address,
    weightLb: point.weightLb ?? 0,
    injected: true,
  };
}
